import {makeAutoObservable, reaction} from 'mobx';

import type {ConferenceExpandedCommon} from '../../../api/conference';
import type {WithReactionDispose} from '../../../utils/store';
import type {RateConferenceConfig} from '../../conference-rating';

import {conferenceStore} from '../../../api/conference';
import {
  isConferenceCorporate,
  isMeConferenceHost,
} from '../../../utils/entity/conference';
import {isCourseIndividual} from '../../../utils/entity/course';
import {emitter, WS_RECEIVE_MESSAGE_EVENT} from '../../../utils/eventEmitter';
import {logger} from '../../../utils/logger';
import {platform} from '../../../utils/platform';
import {ReactionDisposeManager} from '../../../utils/store';
import {showBookNextConferenceAlert} from '../alerts/bookNextConference';
import {LIVE_CONF_AUTO_CLOSE_DELAY_IN_SEC} from './utils';

export type StopConferenceConfig = RateConferenceConfig & {
  courseId: string;
};

type BookNextConferenceAvailabilityHandler = (
  config: StopConferenceConfig,
) => boolean | Promise<boolean>;

export class ConferenceStopHandler implements WithReactionDispose {
  disposer = new ReactionDisposeManager();
  private handledIds = new Map<string, boolean>();
  private handlerTimeout = 0;
  private joinedConference: ConferenceExpandedCommon | null = null;
  private shouldRequestRateAfterBooking = false;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.setupReactions();

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);
  }

  destruct() {
    this.disposer.dispose();
    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);

    clearTimeout(this.handlerTimeout);
  }

  requestConferenceFeedbackIfNeeded() {
    if (this.shouldRequestRateAfterBooking) {
      this.rateConferenceHandler(this.config);

      this.shouldRequestRateAfterBooking = false;
    }
  }

  setBookNextConferenceAvailabilityHandler(
    handler: BookNextConferenceAvailabilityHandler,
  ) {
    this.bookNextConferenceAvailabilityHandler = handler;
  }

  setBookNextConferenceHandler(
    handler: (config: StopConferenceConfig) => void,
  ) {
    this.bookNextConferenceHandler = handler;
  }

  setJoinedConference(conference: ConferenceExpandedCommon | null) {
    this.joinedConference = conference;
  }

  setRateConferenceHandler(handler: (config: RateConferenceConfig) => void) {
    this.rateConferenceHandler = handler;
  }

  private get config(): StopConferenceConfig {
    if (!this.joinedConference) {
      throw new Error('Attempt to get config without joined conference');
    }

    const {_id, type} = this.joinedConference;

    const isB2b = this.isCorporateConference;

    return {
      courseId: this.joinedConference.course_id!,
      id: _id,
      isB2b,
      role: 'client',
      type: type === 'chat' ? 'chat' : 'live',
    };
  }

  private get isCorporateConference() {
    return isConferenceCorporate(this.joinedConference);
  }

  private onStopForClient() {
    if (!this.shouldRun() || !this.joinedConference) {
      return;
    }

    const {type} = this.joinedConference;

    if (!isMeConferenceHost(this.joinedConference)) {
      this.handlerTimeout = +setTimeout(
        () => {
          void this.requestFeedbackForClient();
        },
        type === 'chat' ? 0 : (LIVE_CONF_AUTO_CLOSE_DELAY_IN_SEC + 4) * 1000,
      );
    }
  }

  private onStopForCoach() {
    if (!this.shouldRun() || !this.joinedConference) {
      return;
    }

    if (isMeConferenceHost(this.joinedConference)) {
      this.handlerTimeout = +setTimeout(
        () => {
          this.rateConferenceHandler({
            ...this.config,
            role: 'coach',
          });
        },
        // for mobile app we need to give some time to close conference screen
        platform.isNative ? LIVE_CONF_AUTO_CLOSE_DELAY_IN_SEC * 1000 : 0,
      );
    }
  }

  private onWsMessage(message: any) {
    if (message.event.type === 'conference_stopped') {
      const {conference} = message;

      if (conference) {
        // b2b backend sends two events for one conference
        const isAlreadyHandled = this.handledIds.get(conference._id);

        const wasJoined = conference._id === this.joinedConference?._id;

        if (isAlreadyHandled || !wasJoined) {
          return;
        }

        this.handledIds.set(conference._id, true);

        this.onStopForClient();
      }
    }
  }

  private async requestFeedbackForClient() {
    try {
      if (await this.shouldRequestBookNextConference()) {
        showBookNextConferenceAlert({
          onBook: () => {
            this.bookNextConferenceHandler(this.config);

            this.shouldRequestRateAfterBooking = true;
          },
          onCancel: () => {
            this.rateConferenceHandler(this.config);
          },
        });
      } else {
        this.rateConferenceHandler(this.config);
      }
    } catch (error) {
      logger.error(error);

      this.rateConferenceHandler(this.config);
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'stopConferenceForCoach',
      reaction(
        () => conferenceStore.coach.stopping,
        stopping => {
          if (stopping.success) {
            this.onStopForCoach();
          }
        },
      ),
    );

    this.disposer.watch(
      'joinedConference',
      reaction(
        () => this.joinedConference,
        joinedConference => {
          if (joinedConference) {
            this.shouldRequestRateAfterBooking = false;

            this.handledIds.clear();
          }
        },
      ),
    );
  }

  private async shouldRequestBookNextConference() {
    if (!isCourseIndividual(this.joinedConference?.course)) {
      return false;
    }

    return this.bookNextConferenceAvailabilityHandler(this.config);
  }

  private shouldRun(): boolean {
    if (!this.joinedConference) {
      return false;
    }

    const hasCourse = Boolean(this.joinedConference.course_id);
    const hasSubtenant = Boolean(this.joinedConference.subtenant_id);

    const isIntroCall = !hasCourse;
    const isCorporateIntroCall = isIntroCall && hasSubtenant;

    return !isIntroCall || isCorporateIntroCall;
  }

  private bookNextConferenceAvailabilityHandler: BookNextConferenceAvailabilityHandler =
    () => true;
  private bookNextConferenceHandler = (_config: StopConferenceConfig) => {};
  private rateConferenceHandler = (_config: RateConferenceConfig) => {};
}

export const conferenceStopHandler = new ConferenceStopHandler();
